import React, { useEffect, useState } from 'react'
import { Box, Button, Group, Modal, Paper, Text, Title } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'

const DISCLAIMER_VIEWED_KEY = 'disclaimer_viewed'

// Add window property type declaration
declare global {
  interface Window {
    showDisclaimer?: () => void
  }
}

interface DisclaimerModalProps {
  defaultOpen?: boolean
  alwaysShow?: boolean
}

export default function DisclaimerModal({ defaultOpen = true, alwaysShow = true }: DisclaimerModalProps) {
  const [opened, setOpened] = useState(false)

  useEffect(() => {
    // Check if the user has already seen the disclaimer
    const hasViewedDisclaimer = localStorage.getItem(DISCLAIMER_VIEWED_KEY)

    if ((defaultOpen && !hasViewedDisclaimer) || alwaysShow) {
      setOpened(true)
    }
  }, [defaultOpen, alwaysShow])

  const handleClose = () => {
    // Mark that the user has seen the disclaimer
    localStorage.setItem(DISCLAIMER_VIEWED_KEY, 'true')
    setOpened(false)
  }

  // Function to show the modal - expose via window object
  const showDisclaimer = () => {
    setOpened(true)
  }

  // Make the function globally accessible
  useEffect(() => {
    window.showDisclaimer = showDisclaimer
    return () => {
      delete window.showDisclaimer
    }
  }, [])

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="lg"
      centered
      closeOnEscape={false}
      closeOnClickOutside={false}
      title={
        <Box className="flex items-center gap-3">
          <IconAlertCircle size={24} className="text-blue-600" />
          <Title order={3} className="text-slate-800 font-bold">
            重要声明
          </Title>
        </Box>
      }
    >
      <Paper className="bg-gradient-to-br from-white to-blue-50 p-4">
        <Text className="text-slate-700 font-medium mb-4">尊敬的用户：</Text>
        <Text className="text-slate-600 mb-6 leading-relaxed">
          您好！欢迎使用优查查大数据报告查询服务。为保障您的合法权益，避免不必要的误解和损失，特此郑重声明：
        </Text>

        <Text className="text-slate-600 mb-6 leading-relaxed">
          优查查所提供的报告仅作为大数据信息汇总，旨在为您提供多维度的数据参考，帮助您更全面地了解相关情况。我们明确告知，该报告并非央行征信报告，不具备任何官方信用评估的效力。同时，我们仅专注于大数据报告的查询服务，不涉及任何大数据报告之外的贷款等金融服务。若您对我们的服务有任何疑问或需要进一步了解，建议您及时联系我们的客服人员，我们将竭诚为您解答。
        </Text>

        <Text className="text-slate-600 mb-6 leading-relaxed">
          在使用我们的服务时，请您务必仔细甄别，切勿轻信任何未经授权或虚假宣传的信息。我们始终致力于为您提供真实、可靠的大数据信息，但请您理解，任何数据仅供参考，最终决策还需您根据自身情况谨慎判断。
        </Text>

        <Text className="text-slate-600 mb-4 leading-relaxed">
          感谢您对优查查的信任与支持，我们将持续优化服务，为您创造更多价值。
        </Text>

        <Text className="text-slate-700 text-right font-medium mb-4">优查查团队</Text>

        <Group justify="center" mt="xl">
          <Button
            size="md"
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            onClick={handleClose}
          >
            我已阅读并知晓
          </Button>
        </Group>
      </Paper>
    </Modal>
  )
}
